import { useTableScrollHints } from "../useTableScrollHints";
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { renderMarkdown } from "../md";
import { highlightQuotes, type Highlight } from "../highlight";
import { diffBlocks, diffWords, isEdit } from "../revisionDiff";
import { groupThreads, loadReviewDrafts, saveReviewDrafts, type DocComment, type PendingComment, type Thread } from "../review";

/** Full-size image overlay — click anywhere to dismiss. */
export function Lightbox({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div className="lightbox" onClick={onClose}>
      <img src={src} alt="" />
    </div>
  );
}

/** One entry in a document's revision history, as the switcher lists them. */
export interface DocRevision {
  id: string;
  rev: number;
  ts: number;
  /** The document's own name, which is what a review round announces — the message a document rides on
   *  carries a title of its own, and the two are not the same string. */
  name: string;
}

/** What the panel under the document is showing: a new remark, an open thread, or an unsent remark. */
type Focus = { kind: "new"; quote: string; text: string } | { kind: "thread"; rootId: string; reply: string } | { kind: "pending"; id: string };

const newId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Reader overlay for an attached document (a plan, etc.), loaded on demand. */
export function DocReader({
  doc,
  onClose,
  onPickRevision,
  onSubmitReview,
  loadRevision,
}: {
  doc: {
    title: string;
    content: string | null;
    loading: boolean;
    /** Every revision of this document, oldest first. One entry (or none) means there is nothing to switch
     *  between, and no switcher is drawn. */
    revisions?: DocRevision[];
    /** Which revision is on screen. */
    docId?: string;
    /** Comments already on this document, across revisions. */
    comments?: DocComment[];
  };
  onClose: () => void;
  onPickRevision?: (docId: string) => void;
  /** Submit the whole round at once — the point of batching is that the agent gets one message, not one per
   *  remark. Omitted when the document can't be commented on. */
  onSubmitReview?: (comments: Array<{ quote: string; text: string; parent?: string }>) => Promise<void> | void;
  /** Fetch another revision's text, for comparing it with the one on screen. */
  loadRevision?: (docId: string) => Promise<string | null>;
}) {
  const mdRef = useRef<HTMLDivElement>(null);
  const revisions = doc.revisions ?? [];
  const current = revisions.find((r) => r.id === doc.docId);
  const latest = revisions[revisions.length - 1];
  const [pending, setPending] = useState<PendingComment[]>(() => (doc.docId ? loadReviewDrafts(doc.docId) : []));
  const [focus, setFocus] = useState<Focus | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [listOpen, setListOpen] = useState(() => !!window.matchMedia?.("(min-width: 1000px)").matches);
  const [compare, setCompare] = useState<{ id: string; content: string | null } | null>(null);
  const pressedBackdrop = useRef(false);

  // Unsent remarks belong to the revision they quote, so each revision keeps its own and switching away
  // loses nothing.
  useEffect(() => {
    setPending(doc.docId ? loadReviewDrafts(doc.docId) : []);
    setFocus(null);
    setCompare(null);
  }, [doc.docId]);

  function editPending(next: (p: PendingComment[]) => PendingComment[]) {
    setPending((p) => {
      const n = next(p);
      if (doc.docId) saveReviewDrafts(doc.docId, n);
      return n;
    });
  }

  const threads = useMemo(() => groupThreads(doc.comments ?? []), [doc.comments]);
  const html = useMemo(() => (doc.content == null ? "" : renderMarkdown(doc.content)), [doc.content]);
  const activeKey = focus?.kind === "thread" ? `t:${focus.rootId}` : focus?.kind === "pending" ? `p:${focus.id}` : null;
  const highlights = useMemo<Highlight[]>(() => {
    const cls = (key: string, state: string) => `hl hl-${state}${key === activeKey ? " hl-active" : ""}`;
    return [
      ...threads.map((t) => ({ key: `t:${t.root.id}`, quote: t.root.quote, className: cls(`t:${t.root.id}`, t.root.status === "resolved" ? "resolved" : "open") })),
      ...pending.filter((p) => !p.parent).map((p) => ({ key: `p:${p.id}`, quote: p.quote, className: cls(`p:${p.id}`, "pending") })),
    ];
  }, [threads, pending, activeKey]);
  const [anchored, setAnchored] = useState<Set<string>>(new Set());

  // The body is painted here rather than through React, because the highlights are wrapped into the rendered
  // text and React would otherwise keep a stale copy of what it thinks the markup is.
  useLayoutEffect(() => {
    const root = mdRef.current;
    if (!root) return;
    root.innerHTML = html;
    setAnchored(highlightQuotes(root, highlights));
  }, [html, highlights, compare]);
  useTableScrollHints(mdRef, [html, highlights, compare]);

  function reveal(key: string) {
    mdRef.current?.querySelector(`mark[data-key="${CSS.escape(key)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  /** Offer to comment on whatever is selected, as long as the selection is inside the document body. */
  function offerComment() {
    const sel = window.getSelection();
    const quote = sel?.toString().trim() ?? "";
    if (!quote || !mdRef.current) return;
    const anchor = sel?.anchorNode;
    if (!anchor || !mdRef.current.contains(anchor)) return; // a selection in the chat behind, not the doc
    setFocus({ kind: "new", quote: quote.length > 400 ? `${quote.slice(0, 400)}…` : quote, text: "" });
  }

  function openMark(e: React.MouseEvent) {
    if (window.getSelection()?.toString().trim()) return;
    const key = (e.target as HTMLElement).closest("mark[data-key]")?.getAttribute("data-key");
    if (key?.startsWith("t:")) setFocus({ kind: "thread", rootId: key.slice(2), reply: "" });
    else if (key?.startsWith("p:")) setFocus({ kind: "pending", id: key.slice(2) });
  }

  async function pickCompare(id: string) {
    if (!id) return setCompare(null);
    setFocus(null);
    setCompare({ id, content: null });
    const content = (await loadRevision?.(id)) ?? null;
    setCompare((prev) => (prev?.id === id ? { id, content } : prev));
  }

  const roots = pending.filter((p) => !p.parent);
  const replies = pending.length - roots.length;
  const compareRev = compare && revisions.find((r) => r.id === compare.id);
  const commentable = !!onSubmitReview && !compare;
  const showSide = !compare && (!!focus || (listOpen && (threads.length > 0 || pending.length > 0)));

  return (
    <div
      className="doc-reader-overlay"
      // Only a press that both starts and ends on the backdrop closes the reader: a text selection dragged
      // past the reader's edge ends there too, and must not throw the reader away.
      onMouseDown={(e) => (pressedBackdrop.current = e.target === e.currentTarget)}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) onClose();
      }}
    >
      <div className="doc-reader">
        <div className="doc-reader-bar">
          <span className="doc-reader-title">{doc.title}</span>
          {revisions.length > 1 && (
            <>
              <select className="doc-rev-picker" value={doc.docId ?? ""} onChange={(e) => onPickRevision?.(e.target.value)} aria-label="Revision" title="Revision">
                {/* Newest first: the current version is what you almost always want, and it stays the
                    default selection even after reading back through the history. */}
                {[...revisions].reverse().map((r) => (
                  <option key={r.id} value={r.id}>
                    v{r.rev}
                    {r.id === latest?.id ? " (latest)" : ""} · {new Date(r.ts).toLocaleString()}
                  </option>
                ))}
              </select>
              {loadRevision && (
                <select className="doc-rev-picker" value={compare?.id ?? ""} onChange={(e) => void pickCompare(e.target.value)} aria-label="Compare with" title="Compare with">
                  <option value="">Compare…</option>
                  {[...revisions]
                    .reverse()
                    .filter((r) => r.id !== doc.docId)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        with v{r.rev}
                      </option>
                    ))}
                </select>
              )}
              {/* Reading an old version silently would be a trap — every later edit is missing from it. */}
              {current && latest && current.id !== latest.id && <span className="doc-rev-stale">older version</span>}
            </>
          )}
          <button className="doc-reader-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className={`doc-reader-main${(focus || listOpen) && !compare ? " with-side" : ""}`}>
        {doc.loading ? (
          <div className="doc-reader-body doc-reader-status">Loading…</div>
        ) : doc.content == null ? (
          <div className="doc-reader-body doc-reader-status">Couldn't load this document.</div>
        ) : compare ? (
          <div className="doc-reader-body">
            {compare.content == null ? (
              <div className="doc-reader-status">Loading v{compareRev?.rev}…</div>
            ) : (
              <RevisionDiff
                before={(compareRev?.rev ?? 0) < (current?.rev ?? 0) ? compare.content : doc.content}
                after={(compareRev?.rev ?? 0) < (current?.rev ?? 0) ? doc.content : compare.content}
                label={
                  (compareRev?.rev ?? 0) < (current?.rev ?? 0) ? `v${compareRev?.rev} → v${current?.rev}` : `v${current?.rev} → v${compareRev?.rev}`
                }
                onExit={() => setCompare(null)}
              />
            )}
          </div>
        ) : (
          <div ref={mdRef} className="doc-reader-body md" onMouseUp={commentable ? offerComment : undefined} onClick={openMark} />
        )}

        {showSide && (
          <aside className="doc-side">
            {focus && focus.kind !== "thread" && <FocusPanel focus={focus} setFocus={setFocus} pending={pending} editPending={editPending} />}
            <div className="doc-comments">
              {roots.map((p) => (
                <button
                  key={p.id}
                  className="doc-comment pending"
                  onClick={() => {
                    setFocus({ kind: "pending", id: p.id });
                    reveal(`p:${p.id}`);
                  }}
                >
                  <div className="doc-comment-quote">{p.quote}</div>
                  <div className="doc-comment-text">{p.text}</div>
                  <div className="doc-comment-meta">Not sent yet</div>
                </button>
              ))}
              {threads.map((t) => {
                const open = focus?.kind === "thread" && focus.rootId === t.root.id;
                return (
                  <ThreadCard
                    key={t.root.id}
                    thread={t}
                    active={open}
                    reply={open ? focus.reply : ""}
                    queued={pending.filter((p) => p.parent === t.root.id)}
                    anchored={anchored.has(`t:${t.root.id}`)}
                    commentable={commentable}
                    onOpen={() => {
                      if (open) return;
                      setFocus({ kind: "thread", rootId: t.root.id, reply: "" });
                      reveal(`t:${t.root.id}`);
                    }}
                    onReply={(text) => setFocus({ kind: "thread", rootId: t.root.id, reply: text })}
                    onClose={() => setFocus(null)}
                    editPending={editPending}
                  />
                );
              })}
            </div>
          </aside>
        )}
        </div>

        {!compare && (listOpen || pending.length > 0 || threads.length > 0) && (
          <div className="doc-review-bar">
            <button onClick={() => setListOpen((o) => !o)} aria-expanded={listOpen}>
              {listOpen ? "▾" : "▸"} {pending.length > 0 ? `${plural(roots.length, "comment")}${replies ? `, ${plural(replies, "reply")}` : ""} pending` : plural(threads.length, "thread")}
            </button>
            {pending.length > 0 && (
              <button
                className="primary"
                disabled={submitting}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    await onSubmitReview?.(pending.map(({ quote, text, parent }) => ({ quote, text, ...(parent ? { parent } : {}) })));
                    editPending(() => []);
                    setFocus(null);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitting ? "Sending…" : "Submit review"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** The draft at the top of the comment panel: writing a new remark, or editing an unsent one. */
function FocusPanel({
  focus,
  setFocus,
  pending,
  editPending,
}: {
  focus: Focus;
  setFocus: (f: Focus | null) => void;
  pending: PendingComment[];
  editPending: (next: (p: PendingComment[]) => PendingComment[]) => void;
}) {
  const close = () => setFocus(null);
  const onKey = (submit: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Escape") close();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  if (focus.kind === "new") {
    const add = () => {
      if (!focus.text.trim()) return;
      editPending((p) => [...p, { id: newId(), quote: focus.quote, text: focus.text.trim() }]);
      close();
    };
    return (
      <div className="doc-comment-draft">
        <div className="doc-comment-quote">{focus.quote}</div>
        <textarea autoFocus rows={2} placeholder="Comment on this passage" value={focus.text} onChange={(e) => setFocus({ ...focus, text: e.target.value })} onKeyDown={onKey(add)} />
        <div className="doc-comment-actions">
          <button onClick={close}>Cancel</button>
          <button className="primary" disabled={!focus.text.trim()} onClick={add}>
            Add
          </button>
        </div>
      </div>
    );
  }

  if (focus.kind === "pending") {
    const item = pending.find((p) => p.id === focus.id);
    if (!item) return null;
    return (
      <div className="doc-comment-draft">
        <div className="doc-comment-quote">{item.quote}</div>
        <textarea
          autoFocus
          rows={2}
          value={item.text}
          onChange={(e) => editPending((p) => p.map((x) => (x.id === item.id ? { ...x, text: e.target.value } : x)))}
          onKeyDown={onKey(close)}
        />
        <div className="doc-comment-actions">
          <span className="doc-comment-meta">Not sent yet</span>
          <button
            onClick={() => {
              editPending((p) => p.filter((x) => x.id !== item.id));
              close();
            }}
          >
            Remove
          </button>
          <button className="primary" onClick={close}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/** One comment thread in the side panel: the passage, every message in it, and a reply box while it is open. */
function ThreadCard({
  thread,
  active,
  reply,
  queued,
  anchored,
  commentable,
  onOpen,
  onReply,
  onClose,
  editPending,
}: {
  thread: Thread;
  active: boolean;
  reply: string;
  queued: PendingComment[];
  anchored: boolean;
  commentable: boolean;
  onOpen: () => void;
  onReply: (text: string) => void;
  onClose: () => void;
  editPending: (next: (p: PendingComment[]) => PendingComment[]) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [active]);
  const resolved = thread.root.status === "resolved";
  const messages = [thread.root, ...thread.replies];
  const send = () => {
    const text = reply.trim();
    if (!text) return;
    editPending((p) => [...p, { id: newId(), quote: "", text, parent: thread.root.id }]);
    onReply("");
  };
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  const notes = [resolved && "Resolved", !anchored && "Not in this version"].filter(Boolean).join(" · ");
  return (
    <div ref={ref} className={`doc-comment doc-thread-card${active ? " active doc-thread" : ""}${resolved ? " resolved" : ""}`} onClick={onOpen}>
      <div className="doc-comment-quote">{thread.root.quote}</div>
      {resolved && !active ? (
        <div className="doc-comment-meta">
          {notes} · {plural(messages.length, "comment")}
        </div>
      ) : (
        <>
          {messages.map((c) => (
            <div key={c.id} className={`doc-thread-msg ${c.author}`}>
              <div className="doc-thread-author">
                {c.author === "agent" ? "Agent" : "You"}
                <span className="doc-comment-meta"> · v{c.rev}</span>
              </div>
              {c.author === "agent" ? (
                <div className="doc-comment-text md" dangerouslySetInnerHTML={{ __html: renderMarkdown(c.text) }} />
              ) : (
                <div className="doc-comment-text">{c.text}</div>
              )}
            </div>
          ))}
          {queued.map((q) => (
            <div key={q.id} className="doc-thread-msg user pending">
              <div className="doc-thread-author">
                You
                <span className="doc-comment-meta">
                  {" "}
                  · not sent yet ·{" "}
                  <button className="link" onClick={stop(() => editPending((p) => p.filter((x) => x.id !== q.id)))}>
                    remove
                  </button>
                </span>
              </div>
              <div className="doc-comment-text">{q.text}</div>
            </div>
          ))}
          {notes && <div className="doc-comment-meta">{notes}</div>}
          {commentable && active && (
            <>
              <textarea
                autoFocus
                rows={2}
                placeholder="Reply…"
                value={reply}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onReply(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") onClose();
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <div className="doc-comment-actions">
                <button onClick={stop(onClose)}>Close</button>
                <button className="primary" disabled={!reply.trim()} onClick={stop(send)}>
                  Add reply
                </button>
              </div>
            </>
          )}
          {commentable && !active && <div className="doc-reply-hint">Reply…</div>}
        </>
      )}
    </div>
  );
}

const CONTEXT = 3;

/** Two revisions side by side in one column: shared text as the document, changes marked where they happened. */
function RevisionDiff({ before, after, label, onExit }: { before: string; after: string; label: string; onExit: () => void }) {
  const blocks = useMemo(() => diffBlocks(before, after), [before, after]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const changes = blocks.filter((b) => b.kind === "change").length;
  return (
    <div className="rev-diff">
      <div className="rev-diff-head">
        <span>
          {label} · {changes ? plural(changes, "change") : "no changes"}
        </span>
        <button onClick={onExit}>Done</button>
      </div>
      {blocks.map((b, i) => {
        if (b.kind === "same") {
          const long = b.lines.length > CONTEXT * 2 + 2 && !expanded.has(i);
          if (!long) return <div key={i} className="md" dangerouslySetInnerHTML={{ __html: renderMarkdown(b.lines.join("\n")) }} />;
          const head = i === 0 ? [] : b.lines.slice(0, CONTEXT);
          const tail = i === blocks.length - 1 ? [] : b.lines.slice(-CONTEXT);
          return (
            <Fragment key={i}>
              {head.length > 0 && <div className="md" dangerouslySetInnerHTML={{ __html: renderMarkdown(head.join("\n")) }} />}
              <button className="rev-diff-fold" onClick={() => setExpanded((s) => new Set(s).add(i))}>
                ⋯ {b.lines.length - head.length - tail.length} unchanged lines
              </button>
              {tail.length > 0 && <div className="md" dangerouslySetInnerHTML={{ __html: renderMarkdown(tail.join("\n")) }} />}
            </Fragment>
          );
        }
        const rows: React.ReactNode[] = [];
        let d = 0;
        let a = 0;
        while (d < b.del.length || a < b.add.length) {
          const del = b.del[d];
          const add = b.add[a];
          if (del !== undefined && add !== undefined && isEdit(del, add)) {
            rows.push(
              <div key={`e${d}-${a}`} className="rev-diff-line edit">
                {diffWords(del, add).map((s, k) =>
                  s.kind === "same" ? <span key={k}>{s.text}</span> : s.kind === "del" ? <del key={k}>{s.text}</del> : <ins key={k}>{s.text}</ins>,
                )}
              </div>,
            );
            d++;
            a++;
          } else if (del !== undefined && (add === undefined || d <= a)) {
            rows.push(<div key={`d${d}`} className="rev-diff-line del md" dangerouslySetInnerHTML={{ __html: renderMarkdown(del) }} />);
            d++;
          } else {
            rows.push(<div key={`a${a}`} className="rev-diff-line add md" dangerouslySetInnerHTML={{ __html: renderMarkdown(add!) }} />);
            a++;
          }
        }
        return (
          <div key={i} className="rev-diff-change">
            {rows}
          </div>
        );
      })}
    </div>
  );
}
